import React, { Component } from 'react';
import PropTypes from 'prop-types';
import url from 'url';

import { isUserAgentMobile } from 'common/util/isUserAgentMobileApp';

import { getApartmentUrlPath, getApartmentPageMetadata } from 'common/apartmentHelpers';

import { getListingManagedPhotoUrls } from 'common/listingHelpers';

import { LISTING_STATUS_ACTIVE } from 'common/listingConstants';

import { SCREEN_ID_APARTMENT_PHOTOS } from 'client/constants/screenIds';

import { SR_API_ACTION_GET_APARTMENT_BY_ID } from 'api/actionTypes';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

import Navbar from 'client/components/Navbar';
import ActivityIndicator from 'client/components/ActivityIndicator';
import NavbarButton from 'client/components/NavbarButton';
import SrNavbarToastButton from 'client/components/SrNavbarToastButton';
import SrContactModal from 'client/components/SrContactModal';
import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrFlex from 'client/components/SrFlex';
import SrLightbox from 'client/components/SrLightbox';

import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import ApartmentActions from 'client/actions/Apartment';

export function getShowUnit(floorPlan) {
  const unitsByApartmentUnit = floorPlan.units.reduce(
    (memo, unit) => ({
      ...memo,
      [unit.apartmentUnit]: unit,
    }),
    {},
  );

  const apartmentUnit = floorPlan.tourableApartmentUnits.find((apartmentUnit) => {
    const unit = unitsByApartmentUnit[apartmentUnit];
    return unit && unit.status === LISTING_STATUS_ACTIVE;
  });

  return apartmentUnit ? unitsByApartmentUnit[apartmentUnit] : null;
}

@withTransitionHook
@withScreenId(SCREEN_ID_APARTMENT_PHOTOS)
@withScrollToTop
@withApiState({
  apartmentApiState: {
    action: SR_API_ACTION_GET_APARTMENT_BY_ID,
  },
})
@branch((props) => ({
  userAssignedCustomerSupportPhone: ['currentUser', 'customerSupportPhone'],
  apartment: ['apartments', 'byId', props.match.params.apartmentId],
  floorPlans: ['apartments', 'floorPlansByApartmentId', props.match.params.apartmentId],
  appUrl: ['env', 'APP_URL'],
  userAgent: ['analytics', 'userAgent'],
}))
class ApartmentPhotosPage extends Component {
  static propTypes = {
    userAssignedCustomerSupportPhone: PropTypes.string,
    apartment: PropTypes.object,
    floorPlans: PropTypes.array.isRequired,
    appUrl: PropTypes.string.isRequired,
    apartmentApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    userAgent: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return Promise.all([
      ApartmentActions.getById(inject, {
        apartmentId: parseInt(match.params.apartmentId, 10),
      }),
      ApartmentActions.getFloorPlansByApartmentId(inject, {
        apartmentId: parseInt(match.params.apartmentId, 10),
      }),
    ]);
  }

  static getPageMetadata = (state, match) => {
    const apartment = state.get(['apartments', 'byId', match.params.apartmentId]);
    const appUrl = state.get(['env', 'APP_URL']);

    if (!apartment) {
      return {};
    }

    return getApartmentPageMetadata({ apartment, appUrl });
  };

  constructor(props) {
    super(props);

    this.state = {
      showContactModal: false,
      isLightboxOpen: false,
      lightBoxPhotoIndex: 0,
    };
  }

  _onToggleContactModal = () => {
    this.setState({
      showContactModal: !this.state.showContactModal,
    });
  };

  _openLightbox = (e, index) => {
    e.preventDefault();
    this.setState({
      isLightboxOpen: true,
      lightBoxPhotoIndex: index,
    });
  };

  _onCloseLightbox = () => {
    this.setState({
      isLightboxOpen: false,
    });
  };

  _gotoPrevious = () => {
    const currentLightBoxPhotoIndex = this.state.lightBoxPhotoIndex;
    this.setState({
      lightBoxPhotoIndex: currentLightBoxPhotoIndex - 1,
    });
  };

  _gotoNext = () => {
    const currentLightBoxPhotoIndex = this.state.lightBoxPhotoIndex;
    this.setState({
      lightBoxPhotoIndex: currentLightBoxPhotoIndex + 1,
    });
  };

  render() {
    const {
      userAssignedCustomerSupportPhone,
      apartment,
      floorPlans,
      appUrl,
      apartmentApiState,
      match,
      userAgent,
    } = this.props;

    const { showContactModal, lightBoxPhotoIndex } = this.state;

    const sunroomApartmentLink = apartment && url.resolve(appUrl, getApartmentUrlPath(apartment));
    const isMobileViewport = isUserAgentMobile(userAgent);
    const apartmentPhotos = apartment
      ? [...apartment.photoUrls, ...apartment.communityPhotoUrls]
      : [];
    const units = [];

    if (floorPlans) {
      for (const floorPlan of floorPlans) {
        const unit = getShowUnit(floorPlan);
        if (unit) {
          units.push(unit);
        }
      }
    }

    const navbarProps = {
      leftButton: (
        <SrLink
          to={`/apartment/${match.params.apartmentId}`}
          className="link back"
          restoreScrollPosition
          redirectToLastLocation
        >
          Back
        </SrLink>
      ),
      rightButton: (
        <SrNavbarToastButton
          toastLink={sunroomApartmentLink}
          toastContent="share link copied!"
          buttonContent={
            <img className="share apartment" alt="share apartment" src="/images/share-icon.svg" />
          }
          toggleToast
        />
      ),
      secondaryRightButton: (
        <NavbarButton onClick={this._onToggleContactModal}>
          <img className="phone apartment" alt="phone apartment" src="/images/phone-icon.svg" />
        </NavbarButton>
      ),
    };

    return (
      <div className="SrPage ApartmentPhotosPage page-listing">
        <Navbar {...navbarProps} />
        {apartmentApiState.inProgress ? (
          <ActivityIndicator />
        ) : apartment ? (
          <div className="body">
            <div className="photos">
              <SrLightbox
                photoUrls={apartmentPhotos}
                currentImage={lightBoxPhotoIndex}
                isOpen={this.state.isLightboxOpen}
                onClickPrev={this._gotoPrevious}
                onClickNext={this._gotoNext}
                onClose={this._onCloseLightbox}
                showImageCount={false}
                backdropClosesModal
              />
              <SrFlex direction="col" justify="center" align="center">
                {apartmentPhotos.map((photoUrl, index) => (
                  <div
                    key={photoUrl}
                    className="photoContainer"
                    onClick={(e) => !isMobileViewport && this._openLightbox(e, index)}
                  >
                    <img src={photoUrl} alt={photoUrl} />
                  </div>
                ))}
                {units.map((unit) => {
                  const unitPhotos = getListingManagedPhotoUrls({ listing: unit });
                  const label = `Unit ${unit.apartmentUnit} →`;

                  return unitPhotos.map((photoUrl) => (
                    <div key={photoUrl} className="photoContainer">
                      <img src={photoUrl} alt={photoUrl} />
                      <div className="unitLabel">
                        <SrLink to={`/listing/${unit.id}`}>
                          <SrFlex align="center">
                            <SrIcon className="house-filled-blue-small" />
                            <SrText color="blue300" weight="medium" paddingLeft={6}>
                              {label}
                            </SrText>
                          </SrFlex>
                        </SrLink>
                      </div>
                    </div>
                  ));
                })}
              </SrFlex>
              <div className="photosContainer">
                <SrLink
                  className="button yellow"
                  to={`/apartment/${apartment.id}?scrollTo=floorPlansSection`}
                >
                  request tour
                </SrLink>
              </div>
            </div>
          </div>
        ) : null}
        {showContactModal && (
          <SrContactModal
            isOpen
            onClose={this._onToggleContactModal}
            url={sunroomApartmentLink}
            phoneNumber={userAssignedCustomerSupportPhone}
          />
        )}
      </div>
    );
  }
}

ApartmentPhotosPage.displayName = 'ApartmentPhotosPage';

export default ApartmentPhotosPage;
